import React, { useState, useMemo } from 'react';
import { Space, Input, Button, Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import { SolutionItem } from './SolutionItem';
import { VehicleEditor } from './VehicleEditor';
import { FiltersEditor } from './FiltersEditor';
import { GuideInsights } from './GuideInsights';
import { useDragReorder } from '../hooks/useDragReorder';
import { 
  type GuideContentRequest, 
  type RootVehicleRequest, 
  type VehicleFilterRequest 
} from '@/features/guides/api/generated';
import { type VehicleSelectionValue } from '@/shared/components/VehicleEnrichment';

const { TextArea } = Input;

export interface GuideFormData {
  content: GuideContentRequest;
  rootVehicle: Partial<RootVehicleRequest>;
  filters: VehicleFilterRequest[];
  sourceLocale: string;
}

export type GuideFormMode = 'dashboard' | 'create' | 'edit';

interface GuideFormProps {
  formData: GuideFormData;
  onUpdateContent: (key: keyof GuideContentRequest, value: unknown) => void;
  onUpdateRootVehicle: (selection: VehicleSelectionValue) => void;
  onAddSolution: () => void;
  onUpdateSolution: (index: number, value: string) => void;
  onRemoveSolution: (index: number) => void;
  onReorderSolutions: (fromIndex: number, toIndex: number) => void;
  onAddFilter: (filter: VehicleFilterRequest) => void;
  onRemoveFilter: (index: number) => void;
  onUpdateFilter: (index: number, filter: VehicleFilterRequest) => void;
  mode?: GuideFormMode;
  activeTab?: string;
  onTabChange?: (activeKey: string) => void;
}

export const GuideForm: React.FC<GuideFormProps> = ({
  formData,
  onUpdateContent,
  onUpdateRootVehicle,
  onAddSolution,
  onUpdateSolution,
  onRemoveSolution,
  onReorderSolutions,
  onAddFilter,
  onRemoveFilter,
  onUpdateFilter,
  mode = 'create',
  activeTab,
  onTabChange,
}) => {
  const { t } = useTranslation();
  const [internalActiveTab, setInternalActiveTab] = useState('guide');
  
  const currentActiveTab = activeTab || internalActiveTab;
  const handleTabChange = onTabChange || setInternalActiveTab;

  const {
    draggingIndex,
    dragOverIndex,
    dragOverPosition,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  } = useDragReorder();

  // Memoize tab content to prevent re-creation on every render
  const guideContentTab = useMemo(() => (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <div>
        <div style={{ marginBottom: 8 }}>{t('guides.problem')}</div>
        <Input
          value={formData.content.problem}
          onChange={e => onUpdateContent('problem', e.target.value)}
        />
      </div>
      
      <div>
        <div style={{ marginBottom: 8 }}>{t('guides.solutionsDragInfo')}</div>
        <div>
          {(formData.content.solutions || []).map((solution, idx) => (
            <SolutionItem
              key={idx}
              value={solution}
              index={idx}
              isDragging={draggingIndex === idx}
              isDragOver={dragOverIndex === idx}
              dragOverPosition={dragOverPosition}
              onUpdate={onUpdateSolution}
              onRemove={onRemoveSolution}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e, index) => handleDrop(e, index, onReorderSolutions)}
            />
          ))}
          <Button onClick={onAddSolution}>{t('guides.addSolution')}</Button>
        </div>
      </div>
      
      <div>
        <div style={{ marginBottom: 8 }}>{t('guides.possibleCause')}</div>
        <TextArea
          value={formData.content.possibleCause || ''}
          onChange={e => onUpdateContent('possibleCause', e.target.value || undefined)}
          rows={4}
        />
      </div>
    </Space>
  ), [
    formData.content.problem,
    formData.content.possibleCause,
    formData.content.solutions,
    draggingIndex,
    dragOverIndex,
    dragOverPosition,
    onUpdateContent,
    onAddSolution,
    onUpdateSolution,
    onRemoveSolution,
    onReorderSolutions,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    t
  ]);

  const vehicleTab = useMemo(() => (
    <VehicleEditor
      rootVehicle={formData.rootVehicle}
      onUpdateVehicle={onUpdateRootVehicle}
    />
  ), [formData.rootVehicle, onUpdateRootVehicle]);

  const filtersTab = useMemo(() => (
    <FiltersEditor
      filters={formData.filters}
      onAddFilter={onAddFilter}
      onRemoveFilter={onRemoveFilter}
      onUpdateFilter={onUpdateFilter}
    />
  ), [formData.filters, onAddFilter, onRemoveFilter, onUpdateFilter]);

  const insightsTab = useMemo(() => (
    <GuideInsights />
  ), []);

  // Get tabs based on mode
  const tabs = useMemo(() => {
    const baseTabs = [
      {
        key: 'guide',
        label: t('guides.guide'),
        children: guideContentTab
      },
      {
        key: 'rootVehicle',
        label: t('guides.sourceVehicle'),
        children: vehicleTab
      }
    ];

    if (mode === 'dashboard') {
      // Dashboard mode: only guide + vehicle tabs
      return baseTabs;
    }

    if (mode === 'create') {
      // Create mode: guide + vehicle + filters tabs
      return [
        ...baseTabs,
        {
          key: 'filters',
          label: t('guides.filters'),
          children: filtersTab
        }
      ];
    }

    if (mode === 'edit') {
      // Edit mode: all tabs including insights
      return [
        ...baseTabs,
        {
          key: 'filters',
          label: t('guides.filters'),
          children: filtersTab
        },
        {
          key: 'insights',
          label: t('guides.insights'),
          children: insightsTab
        }
      ];
    }

    return baseTabs;
  }, [mode, t, guideContentTab, vehicleTab, filtersTab, insightsTab]);

  return (
    <Tabs
      activeKey={currentActiveTab}
      onChange={handleTabChange}
      items={tabs}
    />
  );
};